import type { AgentCcpStatusOutputDTO } from '@/types/agents';
import type {
  GetUsersRoutingProfilesOutputDTO,
  UserRoutingProfileItemDTO,
} from '@/types/routingProfile';

type TeamMemberWithEmail = {
  mail?: string | null;
  userPrincipalName?: string | null;
};

/** Prefer Graph mail, fall back to UPN (often the sign-in email). */
export function getTeamMemberEmail(member: TeamMemberWithEmail): string {
  return (member.mail || member.userPrincipalName || '').trim();
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Compare Connect / routing profile ids that may be ARNs or plain ids. */
export function normalizeResourceId(id: string): string {
  const trimmed = id.trim();
  const segment = trimmed.split('/').pop();
  return (segment ?? trimmed).toLowerCase();
}

export function findUserRoutingProfile(
  users: UserRoutingProfileItemDTO[] | undefined,
  member: TeamMemberWithEmail
): UserRoutingProfileItemDTO | undefined {
  if (!users?.length) return undefined;
  const email = normalizeEmail(getTeamMemberEmail(member));
  if (!email) return undefined;
  return users.find(u => normalizeEmail(u.userEmail) === email);
}

export function findAgentCcpStatus(
  statusUsers: AgentCcpStatusOutputDTO[] | undefined,
  connectId: string | undefined
): AgentCcpStatusOutputDTO | undefined {
  if (!statusUsers?.length || !connectId) return undefined;
  const normalized = normalizeResourceId(connectId);
  return statusUsers.find(u => normalizeResourceId(u.connectId) === normalized);
}

export function isOnCallStatus(status?: string): boolean {
  const normalized = status?.toLowerCase() ?? '';
  if (!normalized) return false;
  if (normalized.includes('after call work') || normalized.includes('acw')) {
    return false;
  }
  return (
    normalized.includes('on contact') ||
    normalized.includes('on-call') ||
    normalized.includes('oncall') ||
    normalized.includes('calling') ||
    normalized.includes('connected') ||
    (normalized.includes('busy') && !normalized.includes('pending'))
  );
}

export function getTeamConnectIds(
  usersRoutingProfiles: GetUsersRoutingProfilesOutputDTO | undefined
): string[] {
  return (
    usersRoutingProfiles?.users?.map(u => u.connectId).filter(Boolean) ?? []
  );
}
